import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import Dashboard from "./pages/Dashboard";
import WorkspacePage from "./pages/WorkspacePage";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import AboutPage from "./pages/AboutPage";
import PortfolioPage from "./pages/PortfolioPage";
import ResourcesPage from "./pages/ResourcesPage";
import ContactPage from "./pages/ContactPage";
import ServicePage from "./pages/ServicePage";
import RegisterPage from "./pages/RegisterPage";
import AccountPage from "./pages/AccountPage";
import TemplatesPage from "./pages/TemplatesPage";
import ReportsPage from "./pages/ReportsPage";
import ContactsPage from "./pages/ContactsPage";
import LeadsPage from "./pages/LeadsPage";
import ProjectsPage from "./pages/ProjectsPage";
import HourLogPage from "./pages/HourLogPage";
import PayablesPage from "./pages/PayablesPage";
// iter-120: /workgroup route restored — dedicated settings page opened
// from the user dropdown menu (right under Account).
import WorkgroupSettingsPage from "./pages/WorkgroupSettingsPage";
import PluginsPage from "./pages/PluginsPage";
import PluginDetailPage from "./pages/PluginDetailPage";
import AdminPage from "./pages/AdminPage";
import SubscriptionsPage from "./pages/SubscriptionsPage";
import PublicQuotePage from "./pages/PublicQuotePage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import ReactivatePage from "./pages/ReactivatePage";
import EmailPage from "./pages/EmailPage";
import FilesPage from "./pages/FilesPage";
import ImpersonationBanner from "./components/ImpersonationBanner";
import GlobalAiBubble from "./components/GlobalAiBubble";
import GlobalBottomToolbar from "./components/GlobalBottomToolbar";
import ThemeApplier from "./components/ThemeApplier";
import ProtectedRoute from "./components/ProtectedRoute";
import RouteErrorBoundary from "./components/RouteErrorBoundary";
import InstallPrompt from "./components/InstallPrompt";
import PaywallModal from "./components/PaywallModal";
import { AuthProvider } from "./lib/auth";
import { BrandingProvider } from "./lib/branding";
import { Toaster } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

function App() {
    // iter-126: register the PWA service worker WITH aggressive
    // update polling. On every load we call `registration.update()`
    // so a fresh sw.js (bumped SW_VERSION) is pulled immediately.
    // When a new SW enters "waiting" state we tell it to skipWaiting
    // and hard-reload the page once it takes control — the user
    // never has to uninstall/reinstall or hard-refresh to see a new
    // deploy.
    useEffect(() => {
        if (!("serviceWorker" in navigator)) return;

        let refreshing = false;
        const handleControllerChange = () => {
            if (refreshing) return;
            refreshing = true;
            // Blow local caches once more for good measure, then reload.
            if (window.caches && caches.keys) {
                caches.keys().then((keys) => Promise.all(
                    keys.map((k) => caches.delete(k)),
                )).finally(() => window.location.reload());
            } else {
                window.location.reload();
            }
        };
        navigator.serviceWorker.addEventListener(
            "controllerchange", handleControllerChange,
        );

        const activateWaiting = (reg) => {
            if (reg && reg.waiting) {
                reg.waiting.postMessage({ type: "SKIP_WAITING" });
            }
        };

        const onLoad = async () => {
            let reg;
            try {
                reg = await navigator.serviceWorker.register("/sw.js", {
                    updateViaCache: "none",
                });
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn("SW registration failed", err);
                return;
            }
            // Immediate check + poll every 15 min so long-lived tabs get
            // fresh code too. `visibilitychange` gives us a check as
            // soon as the user comes back to the tab.
            const doCheck = () => reg.update().catch(() => {});
            doCheck();
            const iv = setInterval(doCheck, 15 * 60 * 1000);
            const onVis = () => { if (!document.hidden) doCheck(); };
            document.addEventListener("visibilitychange", onVis);

            // If a waiting worker is already sitting there when we
            // register (previous tab installed but couldn't take
            // control), tell it to activate.
            if (reg.waiting) activateWaiting(reg);
            reg.addEventListener("updatefound", () => {
                const nw = reg.installing;
                if (!nw) return;
                nw.addEventListener("statechange", () => {
                    if (nw.state === "installed" && navigator.serviceWorker.controller) {
                        // A new SW is ready and there's an existing
                        // controller — that means this is an update,
                        // not a first-time install. Activate it.
                        activateWaiting(reg);
                    }
                });
            });
            return () => {
                clearInterval(iv);
                document.removeEventListener("visibilitychange", onVis);
            };
        };
        if (document.readyState === "complete") {
            onLoad();
        } else {
            window.addEventListener("load", onLoad, { once: true });
        }

        return () => {
            navigator.serviceWorker.removeEventListener(
                "controllerchange", handleControllerChange,
            );
        };
    }, []);

    return (
        <div className="App">
            <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
                <AuthProvider>
                    <BrandingProvider>
                        <BrowserRouter>
                        <ImpersonationBanner />
                        <GlobalAiBubble />
                        <Routes>
                            <Route path="/" element={<LandingPage />} />
                            <Route path="/login" element={<LoginPage />} />
                            <Route path="/about" element={<AboutPage />} />
                            <Route path="/portfolio" element={<PortfolioPage />} />
                            <Route path="/resources" element={<ResourcesPage />} />
                            <Route path="/contact" element={<ContactPage />} />
                            <Route path="/services/:slug" element={<ServicePage />} />
                            <Route path="/register" element={<RegisterPage />} />
                            <Route path="/privacy" element={<PrivacyPolicyPage />} />
                            <Route path="/terms" element={<TermsOfServicePage />} />
                            <Route path="/reactivate" element={<ReactivatePage />} />
                            <Route path="/q/:token" element={<PublicQuotePage />} />
                            <Route
                                path="/home"
                                element={
                                    <ProtectedRoute>
                                        <Dashboard />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/workspace/:projectId"
                                element={
                                    <ProtectedRoute>
                                        <RouteErrorBoundary label="project workspace">
                                            <WorkspacePage />
                                        </RouteErrorBoundary>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/account"
                                element={
                                    <ProtectedRoute>
                                        <AccountPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/templates"
                                element={
                                    <ProtectedRoute>
                                        <TemplatesPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/reports"
                                element={
                                    <ProtectedRoute>
                                        <ReportsPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/contacts"
                                element={
                                    <ProtectedRoute>
                                        <ContactsPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/hour-log"
                                element={
                                    <ProtectedRoute>
                                        <HourLogPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/projects"
                                element={
                                    <ProtectedRoute>
                                        <ProjectsPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/leads"
                                element={
                                    <ProtectedRoute>
                                        <LeadsPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/payables"
                                element={
                                    <ProtectedRoute>
                                        <PayablesPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/email"
                                element={
                                    <ProtectedRoute>
                                        <EmailPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/files"
                                element={
                                    <ProtectedRoute>
                                        <FilesPage />
                                    </ProtectedRoute>
                                }
                            />
                            {/* iter-120: /workgroup route — workgroup settings page
                                opened from the user dropdown, right under Account. */}
                            <Route
                                path="/workgroup"
                                element={
                                    <ProtectedRoute>
                                        <WorkgroupSettingsPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/plugins"
                                element={
                                    <ProtectedRoute>
                                        <PluginsPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/plugins/:pluginId"
                                element={
                                    <ProtectedRoute>
                                        <PluginDetailPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin"
                                element={
                                    <ProtectedRoute>
                                        <AdminPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/subscriptions"
                                element={
                                    <ProtectedRoute>
                                        <SubscriptionsPage />
                                    </ProtectedRoute>
                                }
                            />
                        </Routes>
                        <GlobalBottomToolbar />
                        <ThemeApplier />
                        <PaywallModal />
                        </BrowserRouter>
                    </BrandingProvider>
                </AuthProvider>
            </GoogleOAuthProvider>
            <InstallPrompt />
            <Toaster
                theme="dark"
                position="bottom-right"
                toastOptions={{
                    style: {
                        background: "#1C253C",
                        color: "#F1F5F9",
                        border: "1px solid #2A3655",
                    },
                }}
            />
        </div>
    );
}

export default App;
